import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api, { SESSION_EXPIRED_EVENT, waitForSessionRefresh } from "@/lib/api";

const AuthContext = createContext(null);
export const SESSION_CHECK_INTERVAL = 5 * 60 * 1000;
const RECOVERY_INTERVAL = 30 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [connection, setConnection] = useState("checking");
  const currentUser = useRef(null);
  const mounted = useRef(false);
  const generation = useRef(0);
  const pending = useRef(null);
  const controller = useRef(null);
  const retryTimer = useRef(null);
  const slowTimer = useRef(null);
  const lastCheck = useRef(0);

  const updateUser = useCallback((value) => {
    currentUser.current = value;
    setUser(value);
  }, []);

  const cancelCheck = useCallback(() => {
    generation.current += 1;
    controller.current?.abort();
    controller.current = null;
    pending.current = null;
    clearTimeout(retryTimer.current);
    clearTimeout(slowTimer.current);
  }, []);

  const checkSession = useCallback((force = false) => {
    if (!mounted.current) return Promise.resolve();
    if (pending.current) return pending.current;
    if (!force && Date.now() - lastCheck.current < 15000) return Promise.resolve();
    clearTimeout(retryTimer.current);
    if (!navigator.onLine) {
      setConnection("offline");
      return Promise.resolve();
    }
    lastCheck.current = Date.now();
    const checkGeneration = generation.current;
    const active = () => mounted.current && generation.current === checkGeneration;
    const abort = new AbortController();
    controller.current = abort;
    slowTimer.current = setTimeout(() => { if (active()) setConnection("reconnecting"); }, 2000);
    let retry = false;
    pending.current = api.get("/auth/me", { signal: abort.signal }).then(({ data }) => {
      if (!active()) return;
      if (!data?.id) throw new Error("Resposta de sessão inválida");
      updateUser(data);
      setConnection("connected");
    }).catch((err) => {
      if (!active() || err.code === "ERR_CANCELED") return;
      if (err.response?.status === 401) {
        updateUser(false);
        setConnection("connected");
      } else {
        // Keep the current screen and unsaved form state during a temporary outage.
        setConnection(navigator.onLine ? "unavailable" : "offline");
        retry = navigator.onLine;
      }
    }).finally(() => {
      if (!active()) return;
      pending.current = null;
      controller.current = null;
      clearTimeout(slowTimer.current);
      if (retry) retryTimer.current = setTimeout(() => checkSession(true), RECOVERY_INTERVAL);
    });
    return pending.current;
  }, [updateUser]);

  useEffect(() => {
    mounted.current = true;
    checkSession(true);
    const resume = () => { if (currentUser.current !== false) checkSession(); };
    const visible = () => { if (document.visibilityState === "visible") resume(); };
    const online = () => { if (currentUser.current !== false) checkSession(true); };
    const offline = () => { if (currentUser.current !== false) setConnection("offline"); };
    const expired = () => { cancelCheck(); updateUser(false); setConnection("connected"); };
    const interval = setInterval(resume, SESSION_CHECK_INTERVAL);
    window.addEventListener("focus", resume);
    window.addEventListener("pageshow", resume);
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    window.addEventListener(SESSION_EXPIRED_EVENT, expired);
    document.addEventListener("visibilitychange", visible);
    return () => {
      mounted.current = false;
      cancelCheck();
      clearInterval(interval);
      window.removeEventListener("focus", resume);
      window.removeEventListener("pageshow", resume);
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
      window.removeEventListener(SESSION_EXPIRED_EVENT, expired);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [checkSession, cancelCheck, updateUser]);

  const login = async (email, password) => {
    cancelCheck();
    await waitForSessionRefresh();
    const res = await api.post("/auth/login", { email, password });
    updateUser(res.data);
    setConnection("connected");
  };

  const register = async (name, email, password) => {
    cancelCheck();
    await waitForSessionRefresh();
    const res = await api.post("/auth/register", { name, email, password });
    // Registration is an access request, not an authenticated studio session.
    return res.data;
  };

  const logout = async () => {
    cancelCheck();
    await waitForSessionRefresh();
    await api.post("/auth/logout");
    updateUser(false);
    setConnection("connected");
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, connection, retryConnection: () => checkSession(true) }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
